import React, { type CSSProperties, type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IoClose } from "react-icons/io5";
import { OutboundLink } from "gatsby-plugin-google-gtag";
import { Link } from "gatsby-plugin-react-i18next";

import Modal from "@components/modal";
import type { Promo } from "@interfaces/Promo";
import { useConfigurationState } from "@stores/configuration";
import { defaultRipple } from "@utilities/helper";
import { parsePromoLink } from "@utilities/promo";
import DOMPurify from "dompurify";

interface OfferDetailDialogProps {
  promo: Promo;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const OfferDetailDialog: FC<OfferDetailDialogProps> = ({
  promo,
  isOpen,
  setIsOpen,
}) => {
  const { t } = useTranslation("website");
  const configurationState = useConfigurationState();
  const link = parsePromoLink(promo);

  const imageSrc = promo.app_home_image ?? promo.image ?? "";
  const hasImage = imageSrc !== "";

  const innerTitle = (promo.app_inner_title ?? "").trim();
  const innerSubtitle = (promo.app_inner_subtitle ?? "").trim();
  const homeDescription = (promo.app_home_description ?? "").trim();
  const innerDescription = (promo.app_inner_description ?? "").trim();
  const longHtml = (promo.app_inner_long_description ?? "").trim();
  const footerText = (promo.app_footer_text ?? "").trim();

  const trimmedCta = (promo.app_button_text ?? "").trim();
  const ctaLabel = trimmedCta !== "" ? trimmedCta : t("offers.cta_default");
  const secondaryLabel = (promo.app_secondary_button_text ?? "").trim();

  const promoColor = (promo.app_promo_color ?? "").trim();
  const hasColor = /^#[0-9a-fA-F]{3,8}$/.test(promoColor);
  const accentColor = hasColor ? promoColor : "#F97C50"; // primary default

  const primaryStyle: CSSProperties = {
    backgroundColor: accentColor,
    borderColor: accentColor,
    color: "#ffffff",
  };
  const secondaryStyle: CSSProperties = {
    borderColor: accentColor,
    color: accentColor,
    backgroundColor: "transparent",
  };

  const safeHtml = useMemo(() => {
    if (longHtml === "") return "";
    if (typeof window === "undefined") return "";
    return DOMPurify.sanitize(longHtml);
  }, [longHtml]);

  const closeAndContinue = (): void => {
    setIsOpen(false);
  };

  // Primary CTA — also seeds the global coupon code so it auto-applies at
  // checkout when the user lands on the destination route.
  const onPrimaryClick = (): void => {
    const code = (promo.coupon_code ?? "").trim();
    if (code !== "") {
      configurationState.updateCouponCode(code);
    }
    setIsOpen(false);
  };

  const primaryClass =
    "w-full inline-flex items-center justify-center rounded-full border px-6 py-2.5 text-sm font-semibold no-underline transition hover:opacity-90";
  const secondaryClass =
    "w-full inline-flex items-center justify-center rounded-full border px-6 py-2.5 text-sm font-semibold transition hover:opacity-80";

  // `link === null` when the promo's `web_link` is empty — in that case
  // we hide the primary button entirely.
  const primaryButton =
    link === null ? null : link.isInternal ? (
      <Link
        to={link.href}
        onClick={onPrimaryClick}
        className={primaryClass}
        style={primaryStyle}
        ref={defaultRipple}
      >
        {ctaLabel}
      </Link>
    ) : (
      <OutboundLink
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onPrimaryClick}
        className={primaryClass}
        style={primaryStyle}
      >
        {ctaLabel}
      </OutboundLink>
    );

  return (
    <Modal
      isModalOpen={isOpen}
      setIsModalOpen={setIsOpen}
      isCloseButtonExist={false}
      className="!max-w-2xl overflow-hidden"
      // The wrapper is now an ancestor of the panel, so its max-width would
      // clamp the panel below `!max-w-2xl` in the sm→md range. Match it here.
      wrapperClassName="!max-w-2xl"
    >
      {/* The base Modal's close-button row + padding wasted vertical space, so
          we suppress it (isCloseButtonExist={false}) and bleed the content to
          the card edges with negative margins, floating our own compact close
          button over the image. 3-row layout: image (no shrink) · scrollable
          body (flex-1, owns scroll) · pinned footer — so the image stays fully
          visible and uncropped while only the text scrolls on short screens. */}
      <div className="relative -m-4 flex max-h-[80dvh] w-[calc(100%+2rem)] flex-col text-start md:-m-8 md:w-[calc(100%+4rem)]">
        <button
          type="button"
          onClick={() => {
            setIsOpen(false);
          }}
          aria-label={t("offers.close")}
          className="absolute end-3 top-3 z-10 flex size-9 items-center justify-center rounded-full bg-white/80 text-gray-700 shadow-sm backdrop-blur transition duration-300 hover:rotate-90 hover:bg-white"
          ref={defaultRipple}
        >
          <IoClose className="size-5" />
        </button>

        {/* Image — full width, contained so it's never cropped. */}
        {hasImage && (
          <div className="aspect-[16/9] w-full shrink-0 overflow-hidden bg-gray-50">
            <img
              src={imageSrc}
              alt={innerTitle !== "" ? innerTitle : promo.app_home_title}
              className="size-full object-contain"
            />
          </div>
        )}

        {/* Scrollable body — title + copy; gets leftover space and scrolls. */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:px-8 md:pt-6">
          {(innerTitle !== "" || innerSubtitle !== "") && (
            <div className="flex flex-col gap-3">
              {innerTitle !== "" && (
                <h2 className="mb-0 text-2xl font-bold leading-tight text-gray-900 md:text-3xl">
                  {innerTitle}
                </h2>
              )}
              {innerSubtitle !== "" && (
                <p className="mb-0 text-base font-medium text-gray-700">
                  {innerSubtitle}
                </p>
              )}
            </div>
          )}
          {homeDescription !== "" && (
            <>
              <p className="mb-0 text-sm text-gray-600">{homeDescription}</p>
              <div className="w-full border-t border-gray-100" />
            </>
          )}
          {innerDescription !== "" && (
            <p className="mb-0 text-sm text-gray-700">{innerDescription}</p>
          )}
          {safeHtml !== "" && (
            <div
              className="offers-html"
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          )}
          {footerText !== "" && (
            <p className="mb-0 text-xs italic text-gray-500">{footerText}</p>
          )}
        </div>

        {/* Footer — always visible at the bottom of the modal. */}
        <div className="shrink-0 border-t border-gray-100 bg-white px-4 py-3 md:px-8 md:py-4">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {secondaryLabel !== "" && (
              <button
                type="button"
                onClick={closeAndContinue}
                className={secondaryClass}
                style={secondaryStyle}
                ref={defaultRipple}
              >
                {secondaryLabel}
              </button>
            )}
            {primaryButton}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default OfferDetailDialog;
